import { CircularProgress } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import { ExplorerUrl } from '@mogul/shared';
import { fetchBlockNumber } from '@wagmi/core';
import React, { useEffect, useState } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import { useAccount, useNetwork } from 'wagmi';

import { useStarStakingContract } from '../../../../hooks/stars-staking.hook';
import { Button } from '../../../core/button/Button';
import styles from './HarvestModal.module.css';
import { ModalFinishState } from './ModalFinishState.component';

const useStyles = makeStyles(() =>
  createStyles({
    loader: {
      color: 'white',
    },
  }),
);

enum ModalState {
  claim = 'CLAIM',
  finished = 'FINISHED',
  warning = 'WARNING',
}

export interface WithdrawModalProps {
  onClose: () => void;
  withdrawAmount: number | undefined;
  poolId: number;
  depositId: number;
  getRewardInfo: () => void;
  timeLock: number;
  rewardAmount: number;
  poolName: string;
  apr: number;
  endBlock: number;
  starsPerBlock: number;
  totalPoolDepositedLP: number;
}

export const WithdrawModal: React.FC<WithdrawModalProps> = props => {
  const classes = useStyles();
  const { address } = useAccount();
  const { chain } = useNetwork();
  const { withdraw } = useStarStakingContract();
  const [submitting, setSubmitting] = useState(false);
  const [modalState, setModalState] = useState(ModalState.warning);
  const [transactionUrl, setTransactionUrl] = useState('');
  const [warningConsent, setWarningConsent] = useState(false);
  const [possibleRewards, setPossibleRewards] = useState(0);

  const isLpPool = (): boolean => {
    return props.poolName === 'The Oscar’s Pool';
  };

  useEffect(() => {
    if (props.timeLock === 0) {
      setModalState(ModalState.claim);
    }

    const getValues = async () => {
      const currentBlock = await fetchBlockNumber();
      const remainingBlocks = props.endBlock - Number(currentBlock);
      const percentageOfPool =
        (props.withdrawAmount ?? 0) / props.totalPoolDepositedLP;
      const rewardPerBlock = props.starsPerBlock * percentageOfPool;

      setPossibleRewards(remainingBlocks * rewardPerBlock + props.rewardAmount);
    };

    void getValues();
  }, []);

  const handleClose = () => {
    if (submitting) {
      return;
    }

    props.onClose();
  };

  const handleSubmit = async (e: React.FormEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (submitting) {
      return;
    }
    setSubmitting(true);
    try {
      const txn = await withdraw(props.poolId, address!, props.depositId);

      if (!txn.success) {
        setSubmitting(false);
        toast.error(`Withdrawal Failed`);
        return;
      }

      const url = `${chain!.blockExplorers!.default.url}/tx/${
        txn.transactionHash
      }`;

      setTransactionUrl(url);
      setModalState(ModalState.finished);

      toast.success(`Withdrawal Successsful!`);
      props.getRewardInfo();
    } catch (error) {
      setSubmitting(false);
      toast.error(`Withdrawal Failed`);
    }
    setSubmitting(false);
  };

  const handleWarningConsent = () => {
    setWarningConsent(prev => !prev);
  };
  const handleWarningConfirm = () => {
    setModalState(ModalState.claim);
  };

  if (modalState === ModalState.finished) {
    return (
      <>
        <Toaster toastOptions={{ success: { duration: 5000 } }} />
        <ModalFinishState
          header="WITHDRAWAL COMPLETE"
          transactionUrl={transactionUrl}
          handleClose={handleClose}
        />
      </>
    );
  }

  if (modalState === ModalState.warning) {
    return (
      <>
        <div
          className={`${styles.modalBG} relative w-full max-w-screen-sm lg:max-w-full flex flex-col gap-10 justify-center items-center`}
        >
          <div className={styles.title + ' relative'}>
            <div
              className={styles.closeIcon}
              role="button"
              onKeyPress={props.onClose}
              tabIndex={0}
              onClick={props.onClose}
            >
              <CloseIcon />
            </div>
            WARNING
          </div>
          <div className="text-center">
            <div className="mb-4">
              You are about to withdraw your stake early and forfeit earned
              rewards.
            </div>
            {!isLpPool() ? (
              <div className={styles.earnedAmount}>
                {props.withdrawAmount?.toLocaleString()} MOGUL
              </div>
            ) : (
              <div className={styles.earnedAmount}>
                {props.withdrawAmount?.toLocaleString()} MOGUL-BNB
              </div>
            )}
            <div className={styles.caption}>WITHDRAW AMOUNT</div>

            <div className={styles.earnedAmount}>
              {props.rewardAmount.toLocaleString()} MOGUL
            </div>

            <div className={styles.caption}>CURRENT REWARDS WILL FORFEIT</div>

            <div className={styles.earnedAmount}>
              {possibleRewards.toLocaleString()} MOGUL
            </div>

            <div className={styles.caption}>ESTIMATED POSSIBLE REWARDS</div>

            <div className="w-10/12 mx-auto mt-4">
              <div className="flex flex-row">
                <input
                  type="checkbox"
                  id="consent"
                  value={warningConsent.toString()}
                  checked={warningConsent}
                  onClick={handleWarningConsent}
                ></input>
                <div>
                  I understand the nature of forfeiting rewards and wish to
                  continue.
                </div>
              </div>
            </div>
          </div>

          <Button
            onClick={handleWarningConfirm}
            className={
              styles.poolButton +
              ' w-5/6 flex justify-center items-center pointer'
            }
            disabled={!warningConsent}
          >
            CONTINUE
          </Button>
        </div>
      </>
    );
  }

  return (
    <>
      <Toaster toastOptions={{ success: { duration: 5000 } }} />
      <div
        className={`${styles.modalBG} relative w-full max-w-screen-sm lg:max-w-full flex flex-col gap-10 justify-center items-center`}
      >
        {submitting && (
          <div className={styles.starsLoading}>
            <CircularProgress className={`${classes.loader} mb-5`} size={85} />
            <div className={styles.description}>Awaiting Transaction</div>
          </div>
        )}
        <div className={styles.title + ' relative'}>
          <div
            className={styles.closeIcon}
            role="button"
            onKeyPress={props.onClose}
            tabIndex={0}
            onClick={props.onClose}
          >
            <CloseIcon />
          </div>
          WITHDRAW
        </div>
        <div className="text-center">
          {!isLpPool() ? (
            <div className={styles.earnedAmount}>
              {props.withdrawAmount?.toLocaleString()} MOGUL
            </div>
          ) : (
            <div className={styles.earnedAmount}>
              {props.withdrawAmount?.toLocaleString()} MOGUL-BNB
            </div>
          )}
          <div className={styles.caption}>WITHDRAW AMOUNT</div>
        </div>
        <Button
          onClick={handleSubmit}
          className={
            styles.poolButton +
            ' w-5/6 flex justify-center items-center pointer'
          }
          disabled={props.withdrawAmount === 0 || submitting}
        >
          CLAIM
        </Button>
      </div>
    </>
  );
};

export const createWithdrawModal =
  (
    withdrawAmount: number | undefined,
    poolId: number,
    depositId: number,
    getRewardInfo: () => void,
    timeLock: number,
    rewardAmount: number,
    poolName: string,
    apr: number,
    endBlock: number,
    starsPerBlock: number,
    totalPoolDepositedLP: number,
  ) =>
  ({ hideModal }: { hideModal: () => void }) => {
    return (
      <WithdrawModal
        poolId={poolId}
        depositId={depositId}
        withdrawAmount={withdrawAmount}
        onClose={() => hideModal()}
        getRewardInfo={getRewardInfo}
        timeLock={timeLock}
        rewardAmount={rewardAmount}
        poolName={poolName}
        apr={apr}
        endBlock={endBlock}
        starsPerBlock={starsPerBlock}
        totalPoolDepositedLP={totalPoolDepositedLP}
      />
    );
  };
